import { BehaviorSubject, defer, merge, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { computed, inject, Injectable, Injector } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { CORE_CONFIG } from '../config/core-config';
import { StorageService } from '../storage/storage.service';
import { ME_STORAGE_KEY } from '../tenant/instance-profile';
import { TenantConfig } from '../tenant/tenant-config';
import { SESSION_CONFIG, SESSION_HOOKS, SessionHooks, SessionUser } from './session-config';

/** Ventana en la que varios 401 seguidos (peticiones en paralelo) cuentan como uno solo. */
export const UNAUTHORIZED_DEBOUNCE_MS = 2000;

/**
 * TEC-293: el núcleo de la sesión, sin UI. Un solo punto de verdad para el token y el usuario:
 *
 * - **Token** (Sanctum): en Preferences bajo `CORE_CONFIG.tokenKey` (`api_token`), la misma llave que
 *   leen los interceptores de auth y de refresco.
 * - **Usuario**: en Preferences bajo `me` (`ME_STORAGE_KEY`), con el mismo `StorageService` (y por lo
 *   tanto el mismo cifrado) de siempre, así que una sesión iniciada con una versión anterior del app
 *   se lee igual. En memoria vive en `user$` (y su signal `user`).
 * - **Perfil de la instancia**: cada usuario que entra hidrata `TenantConfig` y el logout lo reinicia.
 *
 * Lo propio de cada app (push, stores, overlays, navegación, toasts) entra por `SESSION_HOOKS`; las
 * rutas de los guards y las llaves extra del logout, por `SESSION_CONFIG` (ver `provideSession`).
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  readonly #storage = inject(StorageService);
  readonly #tenant = inject(TenantConfig);
  readonly #tokenKey = inject(CORE_CONFIG).tokenKey;
  readonly #config = inject(SESSION_CONFIG, { optional: true });
  readonly #injector = inject(Injector);

  readonly #userSubject = new BehaviorSubject<SessionUser | null>(null);
  #loggingOut = false;
  #lastUnauthorizedAt = 0;

  /**
   * El usuario en memoria: null, luego el `me` guardado (hidratación al arrancar) y después cada
   * `setUser()` / `clearUser()`. Comparte la última emisión con quien se suscriba tarde.
   */
  readonly user$: Observable<SessionUser | null> = merge(
    this.#userSubject.asObservable(),
    defer(() => this.getStoredUser())
  ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

  /** `user$` como signal. */
  readonly user = toSignal(this.user$, { initialValue: null });
  readonly isLoggedIn = computed(() => this.user() !== null);

  /** El `me` guardado. Es lo que leen los guards: `user$` arranca en null y no se puede esperar. */
  getStoredUser<U extends SessionUser = SessionUser>(): Promise<U | null> {
    return this.#storage.get<U>(ME_STORAGE_KEY);
  }

  /** ¿Hay un usuario guardado? */
  async isAuthenticated(): Promise<boolean> {
    return !!(await this.getStoredUser());
  }

  getToken(): Promise<string | null> {
    return this.#storage.get<string>(this.#tokenKey);
  }

  setToken(token: string): Promise<void> {
    return this.#storage.set(this.#tokenKey, token);
  }

  /**
   * Punto único por el que pasa el usuario que manda el API (login, registro, SSO, `me`, cambio de
   * campus): lo mezcla sobre el actual, lo guarda, hidrata `TenantConfig` y lo emite en `user$`, en
   * ese orden, para que quien lea ambos vea al mismo usuario.
   */
  async setUser<U extends SessionUser>(user: Partial<U>): Promise<void> {
    // Mezcla simple sobre el actual. Toda respuesta con usuario es `me` completo, con `instance` resuelto
    // para el campus de esa respuesta (TEC-354, TEC-380): no quedan banderas de otro campus.
    const updatedUser = { ...(this.#userSubject.getValue() as U | null), ...user } as U;
    await this.#storage.set<U>(ME_STORAGE_KEY, updatedUser);
    this.#tenant.hydrate(updatedUser);
    this.#userSubject.next(updatedUser);
  }

  /**
   * Inicio de sesión: el token se guarda antes de emitir al usuario. La emisión enciende las queries
   * que dependen del usuario, y dispararlas sin token da un 401 que cerraría la sesión recién abierta.
   */
  async setSession<U extends SessionUser>(user: Partial<U>, token: string): Promise<void> {
    await this.setToken(token);
    await this.setUser<U>(user);
  }

  /** Saca al usuario de memoria: borra `me`, reinicia `TenantConfig` y emite null. No toca el token. */
  async clearUser(): Promise<void> {
    await this.#storage.remove(ME_STORAGE_KEY);
    this.#tenant.reset();
    this.#userSubject.next(null);
  }

  /**
   * Cierra la sesión. Una sola a la vez: los llamados mientras corre se ignoran. El orden (y qué
   * se espera) está documentado en `SessionHooks`.
   */
  async logout(): Promise<void> {
    if (this.#loggingOut) return;
    this.#loggingOut = true;

    try {
      const hooks = this.#hooks();
      await hooks?.beforeLogout?.();

      // Sin esperar: quien lee `user$` deja de ver al usuario cuanto antes, sin frenar lo demás.
      void this.clearUser();

      await hooks?.onLogout?.();

      await Promise.all(
        [ME_STORAGE_KEY, this.#tokenKey, ...(this.#config?.clearOnLogout ?? [])].map((key) => this.#storage.remove(key))
      );

      await hooks?.afterLogout?.();
    } finally {
      this.#loggingOut = false;
    }
  }

  /**
   * Para el interceptor de errores: el API respondió 401. Varios 401 dentro de
   * `UNAUTHORIZED_DEBOUNCE_MS` cuentan como uno. Llama a `onUnauthorized` del app o, sin él, a `logout()`.
   */
  handleUnauthorized(): void {
    const now = Date.now();
    if (now - this.#lastUnauthorizedAt <= UNAUTHORIZED_DEBOUNCE_MS) return;
    this.#lastUnauthorizedAt = now;

    const hooks = this.#hooks();
    if (hooks?.onUnauthorized) {
      hooks.onUnauthorized();
    } else {
      void this.logout();
    }
  }

  #hooks(): SessionHooks | null {
    return this.#injector.get(SESSION_HOOKS, null);
  }
}
